import InfoIcon from '@mui/icons-material/Info';
import { Tooltip } from '@mui/material';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { PhoneNumberInput } from '@parkchirp/shared-ui-components';

import {
	SetShowDialog,
	SetShowSnackbar,
	SubscriptionManualTaxExemption,
	SubscriptionTaxExemptStatus,
} from '../../types';
import { FILE_UPLOAD_MAXIMUM, isOwnerPortal } from '../../utils';
import { FileUploadTable } from '../fileUploadTable';
import { ReassignPrimaryContactModal } from '../modals';
import { BillingAddress, PayorAddress } from './address';
import { PayorName } from './payorName';
import { SubscriptionInvoiceEmailTable } from './subscriptionInvoiceEmailTable';
import { TaxExemptionsSelect } from './taxExemptionsSelect';

interface EditSubscriptionInputProps {
	refetch: any;
	isEditMode: boolean;
	setHasUnsavedChanges: any;
	subscriptionName: string;
	payorFirstName: string;
	payorLastName: string;
	payorId: string;
	payorCognitoSub: string;
	payorWordpressUserId: number;
	accountId?: string;
	taxExempt: SubscriptionTaxExemptStatus;
	manualTaxExemptions: SubscriptionManualTaxExemption;
	payorType: string;
	payorReadableId?: string;
	status?: string;
	billingProfile: any;
	payorBillingPhone: any;
	payorBillingPhoneCode: number;
	payorBillingWorkPhone: any;
	payorBillingWorkPhoneCode: number;
	payorBillingEmail: any;
	subscriptionNotes: string;
	subscriptionInvoiceNotes: string;
	infoAttention: string;
	payorCity: string;
	payorState: string;
	payorStreet: string;
	payorZip: string;
	billingCompanyName: string;
	billingAddressStreet2: string;
	billingCity: string;
	billingState: string;
	billingStreet: string;
	billingZip: string;
	emails: any;
	mailInvoices?: string;
	emailInvoices?: string;
	files: any;
	bucket: string;
	bucketDir: string;
	onAddFiles: any;
	onDeleteFile: any;
	setSubscriptionName: any;
	setPayorFirstName: any;
	setPayorLastName: any;
	setTaxExempt: any;
	setManualTaxExemptions: (manualTaxExemptions: SubscriptionManualTaxExemption) => void;
	setPayorType: any;
	setPayorBillingPhone: any;
	setPayorBillingPhoneCode: any;
	setPayorBillingPhoneIsValid: any;
	setPayorBillingWorkPhone: any;
	setPayorBillingWorkPhoneCode: any;
	setPayorBillingWorkPhoneIsValid: any;
	setPayorBillingEmail: any;
	setSubscriptionNotes: any;
	setSubscriptionInvoiceNotes: any;
	setInfoAttention: any;
	setPayorCity: any;
	setPayorState: any;
	setPayorStreet: any;
	setPayorZip: any;
	setBillingCompanyName: any;
	setBillingAddressStreet2: any;
	setBillingCity: any;
	setBillingState: any;
	setBillingStreet: any;
	setBillingZip: any;
	setEmails: any;
	setMailInvoices: any;
	setEmailInvoices: any;
	setShowDialog: SetShowDialog;
	setShowSnackbar: SetShowSnackbar;
	tenantSubscriptionId?: string;
	residentNonResident: string | null;
	setResidentNonResident: (x: string | null) => void;
	residentUnit: string | null;
	setResidentUnit: (x: string | null) => void;
	tenantNonTenant: string | null;
	setTenantNonTenant: (tenantNonTenant: string) => void;
	tenantName: string | null;
	setTenantName: (x: string | null) => void;
}

export const EditSubscriptionInputs = ({
	refetch,
	isEditMode,
	setHasUnsavedChanges,
	subscriptionName,
	payorFirstName,
	payorLastName,
	payorId,
	payorCognitoSub,
	payorWordpressUserId,
	accountId,
	taxExempt,
	manualTaxExemptions,
	payorType,
	payorReadableId,
	status,
	billingProfile,
	payorBillingPhone,
	payorBillingPhoneCode,
	payorBillingWorkPhone,
	payorBillingWorkPhoneCode,
	payorBillingEmail,
	subscriptionNotes,
	subscriptionInvoiceNotes,
	infoAttention,
	payorCity,
	payorState,
	payorStreet,
	payorZip,
	billingCompanyName,
	billingAddressStreet2,
	billingCity,
	billingState,
	billingStreet,
	billingZip,
	emails,
	mailInvoices,
	emailInvoices,
	files,
	bucket,
	bucketDir,
	onAddFiles,
	onDeleteFile,
	setSubscriptionName,
	setPayorFirstName,
	setPayorLastName,
	setTaxExempt,
	setManualTaxExemptions,
	setPayorType,
	setPayorBillingPhone,
	setPayorBillingPhoneCode,
	setPayorBillingWorkPhone,
	setPayorBillingPhoneIsValid,
	setPayorBillingWorkPhoneCode,
	setPayorBillingWorkPhoneIsValid,
	setPayorBillingEmail,
	setSubscriptionNotes,
	setSubscriptionInvoiceNotes,
	setInfoAttention,
	setPayorCity,
	setPayorState,
	setPayorStreet,
	setPayorZip,
	setBillingCompanyName,
	setBillingAddressStreet2,
	setBillingCity,
	setBillingState,
	setBillingStreet,
	setBillingZip,
	setEmails,
	setMailInvoices,
	setEmailInvoices,
	setShowDialog,
	setShowSnackbar,
	tenantSubscriptionId,
	residentNonResident,
	setResidentNonResident,
	residentUnit,
	setResidentUnit,
	tenantNonTenant,
	setTenantNonTenant,
	tenantName,
	setTenantName,
}: EditSubscriptionInputProps) => {
	const setModal = (emailDefaultValue = null) =>
		setShowDialog(
			<ReassignPrimaryContactModal
				{...{
					payorId,
					payorCognitoSub,
					payorWordpressUserId,
					emailDefaultValue,
					handleConfirmationModalClose,
					handleClose: handleModalClose,
					oldPayorAttention: infoAttention,
					setShowSnackbar,
					setShowDialog,
					oldBillingCompanyName: billingCompanyName,
					oldBillingAddressStreet2: billingAddressStreet2,
					oldBillingCity: billingCity,
					oldBillingState: billingState,
					oldBillingStreet: billingStreet,
					oldBillingZip: billingZip,
					oldPayorCity: payorCity,
					oldPayorState: payorState,
					oldPayorStreet: payorStreet,
					oldPayorZip: payorZip,
				}}
			/>,
		);
	// we have a separate close function for the reassign primary contact
	// confirmation modals ("are you sure you want to reassign <email>?")
	// so that when the original modal re-appears, the email that the user
	// entered is still present in the text box.

	const handleConfirmationModalClose = (emailDefaultValue = null) => setModal(emailDefaultValue);
	const handleModalClose = (shouldRefetch) => {
		if (shouldRefetch) {
			refetch();
		}
		return setShowDialog(null);
	};

	return (
		<>
			<Typography paddingTop='40px' variant='h6' gutterBottom marginBottom='15px'>
				General
			</Typography>
			<Grid className='subscriptionInfoGrid' container spacing={3}>
				{!tenantSubscriptionId && (
					<Grid item xs>
						<TextField
							required
							id='name'
							name='name'
							label='Subscription Name'
							fullWidth
							value={subscriptionName}
							onChange={(e) => {
								setHasUnsavedChanges(true);
								setSubscriptionName(e.target.value);
							}}
							autoComplete='name'
							InputProps={{
								readOnly: !isEditMode,
							}}
							inputProps={{
								'data-cy': 'subscription-name-input',
							}}
						/>
					</Grid>
				)}
				{!tenantSubscriptionId && (
					<Grid item xs>
						<TextField
							required
							id='account-id'
							label='Account ID'
							fullWidth
							disabled
							value={accountId}
							InputProps={{
								readOnly: !isEditMode,
							}}
							inputProps={{ 'data-cy': 'account-id-input' }}
						/>
					</Grid>
				)}
				{!tenantSubscriptionId && taxExempt !== 'Certificate' && (
					<>
						<Grid item xs>
							<FormControl fullWidth required>
								<InputLabel id='tax-exempt-label'>Tax Exempt</InputLabel>
								<Select
									labelId='tax-exempt-label'
									id='tax-exempt'
									label='Tax Exempt'
									value={taxExempt || ''}
									onChange={(e) => {
										setHasUnsavedChanges(true);
										setTaxExempt(e.target.value);
									}}
									inputProps={{
										readOnly: !isEditMode,
									}}
									data-cy='tax-exempt-select-menu'
								>
									<MenuItem value='No'>No</MenuItem>
									<MenuItem value='Manual'>Manual</MenuItem>
								</Select>
							</FormControl>
						</Grid>
						{taxExempt === 'Manual' && (
							<Grid item xs>
								<TaxExemptionsSelect
									exemptions={manualTaxExemptions}
									onChange={(newExemptions) => {
										setHasUnsavedChanges(true);
										setManualTaxExemptions(newExemptions);
									}}
									disabled={!isEditMode}
								/>
							</Grid>
						)}
					</>
				)}
				<Grid item xs>
					<FormControl fullWidth required>
						<InputLabel id='mail-invoice-label'>Mail Invoices</InputLabel>
						<Select
							labelId='mail-invoice-label'
							id='mail-invoice'
							label='Mail Invoices'
							value={mailInvoices || ''}
							onChange={(e) => {
								setHasUnsavedChanges(true);
								setMailInvoices(e.target.value);
							}}
							inputProps={{
								readOnly: !isEditMode,
							}}
							data-cy='mail-invoices-select-menu'
						>
							<MenuItem value='Yes'>Yes</MenuItem>
							<MenuItem value='No'>No</MenuItem>
						</Select>
					</FormControl>
				</Grid>
				<Grid item xs>
					<FormControl fullWidth required>
						<InputLabel id='email-invoice-label'>Email Invoices</InputLabel>
						<Select
							labelId='email-invoice-label'
							id='email-invoice'
							label='Email Invoices'
							value={emailInvoices || ''}
							onChange={(e) => {
								setHasUnsavedChanges(true);
								setEmailInvoices(e.target.value);
							}}
							inputProps={{
								readOnly: !isEditMode,
							}}
							data-cy='email-invoices-select-menu'
						>
							<MenuItem value='Yes'>Yes</MenuItem>
							<MenuItem value='No'>No</MenuItem>
						</Select>
					</FormControl>
				</Grid>
			</Grid>

			<Grid className='subscriptionInfoGrid' container spacing={3}>
				{!tenantSubscriptionId && (
					<>
						<Grid item xs={3}>
							<FormControl fullWidth required>
								<InputLabel id='subscriber-type-label'>Subscriber Type</InputLabel>
								<Select
									required
									labelId='subscriber-type-label'
									id='subscriber-type-id'
									label='Subscriber Type'
									fullWidth
									// business or individual
									value={payorType || ''}
									onChange={(e) => {
										setHasUnsavedChanges(true);
										setPayorType(e.target.value);
									}}
									inputProps={{
										readOnly: !isEditMode,
									}}
									data-cy='subscriber-type-select-menu'
								>
									<MenuItem value='Business'>Business</MenuItem>
									<MenuItem value='Individual'>Individual</MenuItem>
								</Select>
							</FormControl>
						</Grid>
						<Grid item xs>
							<TextField
								required
								disabled
								id='subscriber-id'
								label='Subscriber ID'
								fullWidth
								value={payorReadableId}
								InputProps={{
									readOnly: !isEditMode,
								}}
								inputProps={{ 'data-cy': 'subscriber-id-input' }}
							/>
						</Grid>
					</>
				)}
				<Grid item xs>
					<TextField
						required
						id='subscription-status'
						label='Subscription Status'
						fullWidth
						disabled
						value={status}
						InputProps={{
							readOnly: !isEditMode,
						}}
						inputProps={{ 'data-cy': 'subscription-status-input' }}
					/>
				</Grid>
			</Grid>
			{!tenantSubscriptionId && (
				<Grid className='subscriptionInfoGrid' container spacing={3}>
					<Grid item xs={3}>
						<FormControl fullWidth>
							<InputLabel id='resident-non-resident-label'>Resident / Non-Resident</InputLabel>
							<Select
								labelId='resident-non-resident-label'
								id='resident-non-resident-id'
								label='Resident / Non-Resident'
								fullWidth
								value={residentNonResident ?? ''}
								onChange={(e) => {
									setHasUnsavedChanges(true);
									setResidentNonResident(e.target.value);
								}}
								inputProps={{
									readOnly: !isEditMode,
								}}
								data-cy='resident-non-resident-select-menu'
							>
								<MenuItem value={null}>&nbsp;</MenuItem>
								<MenuItem value='R'>R</MenuItem>
								<MenuItem value='NR'>NR</MenuItem>
							</Select>
						</FormControl>
					</Grid>
					<Grid item xs={3}>
						<TextField
							id='resident-unit'
							label='Resident Unit'
							fullWidth
							value={residentUnit ?? ''}
							onChange={(e) => {
								setHasUnsavedChanges(true);
								setResidentUnit(e.target.value || null);
							}}
							InputProps={{
								readOnly: !isEditMode,
							}}
							data-cy='resident-unit-input'
						/>
					</Grid>
					<Grid item xs={3}>
						<FormControl fullWidth>
							<InputLabel id='tenant-non-tenant-label'>Tenant / Non-Tenant</InputLabel>
							<Select
								labelId='tenant-non-tenant-label'
								id='tenant-non-tenant-id'
								label='Tenant / Non-Tenant'
								fullWidth
								value={tenantNonTenant ?? ''}
								onChange={(e) => {
									setHasUnsavedChanges(true);
									setTenantNonTenant(e.target.value);
								}}
								inputProps={{
									readOnly: !isEditMode,
								}}
								data-cy='tenant-non-tenant-select-menu'
							>
								<MenuItem value={null}>&nbsp;</MenuItem>
								<MenuItem value='T'>T</MenuItem>
								<MenuItem value='NT'>NT</MenuItem>
							</Select>
						</FormControl>
					</Grid>
					<Grid item xs={3}>
						<TextField
							id='tenant-name'
							label='Tenant Name'
							fullWidth
							value={tenantName ?? ''}
							onChange={(e) => {
								setHasUnsavedChanges(true);
								setTenantName(e.target.value || null);
							}}
							InputProps={{
								readOnly: !isEditMode,
							}}
							data-cy='tenant-name-input'
						/>
					</Grid>
				</Grid>
			)}
			<Grid className='subscriptionInfoGrid' container spacing={3}>
				<Grid item xs>
					<TextField
						required
						id='billing-profile'
						label='Billing Profile'
						fullWidth
						disabled
						value={billingProfile}
						InputProps={{
							readOnly: !isEditMode,
						}}
						inputProps={{ 'data-cy': 'billing-profile-select-menu' }}
					/>
				</Grid>
			</Grid>
			<Typography variant='h6' gutterBottom marginBottom='15px'>
				Primary Contact
			</Typography>
			<Grid
				className='subscriptionInfoGrid'
				container
				sx={{ flexDirection: 'column', width: '25%', minWidth: '350px' }}
				spacing={3}
			>
				<PayorName
					payorFirstName={payorFirstName}
					payorLastName={payorLastName}
					isEditMode={isEditMode}
					setPayorFirstName={setPayorFirstName}
					setPayorLastName={setPayorLastName}
					setHasUnsavedChanges={setHasUnsavedChanges}
				/>
				<Grid item xs>
					<PhoneNumberInput
						phoneNumber={payorBillingPhone || ''}
						countryCode={payorBillingPhoneCode || 1}
						readOnly={!isEditMode}
						onPhoneChange={(phone) => {
							setHasUnsavedChanges(true);
							setPayorBillingPhone(phone);
						}}
						onCountryCodeChange={(code) => {
							setHasUnsavedChanges(true);
							setPayorBillingPhoneCode(code);
						}}
						onValidityChange={setPayorBillingPhoneIsValid}
					/>
				</Grid>
				<Grid item xs>
					<PhoneNumberInput
						label='Work Phone'
						placeholder='Work Phone Number'
						phoneNumber={payorBillingWorkPhone || ''}
						countryCode={payorBillingWorkPhoneCode || 1}
						readOnly={!isEditMode}
						onPhoneChange={(phone) => {
							setHasUnsavedChanges(true);
							setPayorBillingWorkPhone(phone);
						}}
						onCountryCodeChange={(code) => {
							setHasUnsavedChanges(true);
							setPayorBillingWorkPhoneCode(code);
						}}
						onValidityChange={setPayorBillingWorkPhoneIsValid}
					/>
				</Grid>
				<Grid item xs>
					<TextField
						required
						disabled
						id='work-email'
						label='Email'
						fullWidth
						value={payorBillingEmail || ''}
						onChange={(e) => {
							setHasUnsavedChanges(true);
							setPayorBillingEmail(e.target.value);
						}}
						InputProps={{
							readOnly: true,
						}}
						inputProps={{
							'data-cy': 'primary-contact-email-input',
						}}
					/>
				</Grid>
				{!tenantSubscriptionId && !isOwnerPortal && (
					<Grid item xs>
						<Button
							variant='contained'
							onClick={() => setModal()}
							data-cy='reassign-primary-contact-button'
						>
							Reassign Primary Contact
						</Button>
					</Grid>
				)}
			</Grid>
			<Grid className='subscriptionInfoGrid' item xs={12}>
				<PayorAddress
					payorStreet={payorStreet}
					payorCity={payorCity}
					payorState={payorState}
					payorZip={payorZip}
					setPayorStreet={setPayorStreet}
					setPayorCity={setPayorCity}
					setPayorState={setPayorState}
					setPayorZip={setPayorZip}
					isEditMode={isEditMode}
					setHasUnsavedChanges={setHasUnsavedChanges}
				/>
			</Grid>
			<Typography variant='h6' gutterBottom marginTop='10px'>
				Invoice Email Recipients
			</Typography>
			<Grid className='subscriptionInfoGrid subscriptionBillingEmails' item xs={6}>
				<p>
					Select Edit button to add up to 5 Invoice email recipients (this includes all monthly Invoice
					emails)
				</p>
				<SubscriptionInvoiceEmailTable
					{...{
						rows: emails,
						setRows: setEmails,
						isEditMode,
						setShowSnackbar,
					}}
				/>
			</Grid>
			<Typography variant='h6' gutterBottom marginBottom='15px'>
				Billing Address
			</Typography>
			<Grid className='subscriptionInfoGrid' item xs={12}>
				<Grid item xs marginTop='10px' marginBottom='10px'>
					<TextField
						id='name'
						name='name'
						label='Billing Name (Company Name)'
						fullWidth
						value={billingCompanyName || ''}
						onChange={(e) => {
							setHasUnsavedChanges(true);
							setBillingCompanyName(e.target.value);
						}}
						autoComplete='name'
						InputProps={{
							readOnly: !isEditMode,
						}}
						data-cy='billing-address-company-name-input'
					/>
				</Grid>
				<Grid item xs marginTop='10px' marginBottom='10px'>
					<TextField
						id='name'
						name='name'
						label='Attention'
						fullWidth
						value={infoAttention || ''}
						onChange={(e) => {
							setHasUnsavedChanges(true);
							setInfoAttention(e.target.value);
						}}
						autoComplete='name'
						InputProps={{
							readOnly: !isEditMode,
						}}
						data-cy='billing-attention-input'
					/>
				</Grid>
				<BillingAddress
					billingStreet={billingStreet}
					billingAddressStreet2={billingAddressStreet2}
					billingCity={billingCity}
					billingState={billingState}
					billingZip={billingZip}
					setBillingStreet={setBillingStreet}
					setBillingAddressStreet2={setBillingAddressStreet2}
					setBillingCity={setBillingCity}
					setBillingState={setBillingState}
					setBillingZip={setBillingZip}
					isEditMode={isEditMode}
					setHasUnsavedChanges={setHasUnsavedChanges}
				/>
			</Grid>
			{!tenantSubscriptionId && (
				<>
					<Typography
						style={{ display: 'flex', alignItems: 'center' }}
						variant='h6'
						gutterBottom
						marginTop='10px'
					>
						Subscription Invoice Notes
						<Tooltip
							className='info-icon'
							title='These notes will appear on invoices'
							placement='right'
							arrow
						>
							<InfoIcon fontSize='small' />
						</Tooltip>
					</Typography>
					<Grid className='subscriptionInfoGrid' item xs={12}>
						<TextField
							id='sub-notes'
							name='notes'
							multiline
							rows={4}
							fullWidth
							value={subscriptionInvoiceNotes || ''}
							onChange={(e) => {
								setHasUnsavedChanges(true);
								setSubscriptionInvoiceNotes(e.target.value);
							}}
							autoComplete='notes'
							InputProps={{
								readOnly: !isEditMode,
							}}
							inputProps={{
								'data-cy': 'subscription-invoice-notes-multiline',
							}}
						/>
					</Grid>
					<Typography
						style={{ display: 'flex', alignItems: 'center' }}
						variant='h6'
						gutterBottom
						marginTop='10px'
					>
						Subscription Notes
						<Tooltip
							className='info-icon'
							title='These notes are for internal purposes only'
							placement='right'
							arrow
						>
							<InfoIcon fontSize='small' />
						</Tooltip>
					</Typography>
					<Grid className='subscriptionInfoGrid' item xs={12}>
						<TextField
							id='subscription-notes'
							name='subscription-notes'
							multiline
							rows={4}
							fullWidth
							value={subscriptionNotes || ''}
							onChange={(e) => {
								setHasUnsavedChanges(true);
								setSubscriptionNotes(e.target.value);
							}}
							autoComplete='notes'
							InputProps={{
								readOnly: !isEditMode,
							}}
							inputProps={{
								'data-cy': 'contact-information-notes-multiline',
							}}
						/>
					</Grid>
					<Typography variant='h6' gutterBottom marginTop='10px'>
						Supporting Documents
					</Typography>
					<Grid className='subscriptionInfoGrid' item xs={12}>
						<Typography variant='body1' sx={{ marginTop: '10px', fontSize: '12px' }}>
							{`(${files.length} / ${FILE_UPLOAD_MAXIMUM})`}
						</Typography>
						<FileUploadTable
							bucket={bucket}
							bucketDir={bucketDir}
							includeTimestamp
							files={files}
							setFiles={onAddFiles}
							handleDeleteFile={onDeleteFile}
							useAppContext={() => ({ setShowSnackbar })}
						/>
					</Grid>
				</>
			)}
		</>
	);
};
